import type { components, Page } from "@theta/api-client";
import { thetaApi, dataOf, pageOf, unwrapV1 } from "./theta";

type Schemas = components["schemas"];

/**
 * GROUP RULES (OSO-648) — `/groups/{ref}/rules`.
 *
 * The numbered list a moderator can cite when hiding or deleting content.
 * Reads follow the group's SHELL (a prospective member reads the rules before
 * applying); writes are the group's admins. The list caps at 15 server-side —
 * `MAX_GROUP_RULES` mirrors that so the editor can grey out "Tilføj" at the
 * same number instead of collecting a 422.
 */
export type GroupRule = Schemas["GroupRule"];
type GroupRef = string | number;

export const MAX_GROUP_RULES = 15;

export function getGroupRulesPage(
  ref: GroupRef,
  cursor?: string | null,
): Promise<Page<GroupRule>> {
  return thetaApi.http
    .GET("/groups/{ref}/rules", {
      params: { path: { ref: String(ref) }, query: cursor ? { cursor } : {} },
    })
    .then((r) => pageOf<GroupRule>(r));
}

export function createGroupRule(
  ref: GroupRef,
  input: { title: string; body?: string | null },
): Promise<GroupRule> {
  return thetaApi.http
    .POST("/groups/{ref}/rules", {
      params: { path: { ref: String(ref) } },
      body: { title: input.title, body: input.body ?? null },
    })
    .then((r) => dataOf<GroupRule>(r));
}

/** §1.7: absent = untouched. `position` moves the rule and shifts the rest. */
export function updateGroupRule(
  ref: GroupRef,
  ruleId: number,
  patch: { title?: string; body?: string | null; position?: number },
): Promise<GroupRule> {
  return thetaApi.http
    .PATCH("/groups/{ref}/rules/{id}", {
      params: { path: { ref: String(ref), id: ruleId } },
      body: patch,
    })
    .then((r) => dataOf<GroupRule>(r));
}

export async function deleteGroupRule(ref: GroupRef, ruleId: number): Promise<void> {
  unwrapV1<null>(
    await thetaApi.http.DELETE("/groups/{ref}/rules/{id}", {
      params: { path: { ref: String(ref), id: ruleId } },
    }),
  );
}
